/**
 * Identity normalization layer per multi-provider WhatsApp.
 *
 * Risolve il mismatch tra:
 * - WAHA (provider 'whatsapp'): JID format `<E164>@c.us` / `<group_id>@g.us` / `<lid_id>@lid`
 * - Cloud API (provider 'whatsapp_cloud' / 'whatsapp_cloud_coex'): E.164 puro come `wa_id`
 *
 * Per join cross-provider (matching `clients.phone_whatsapp`, inbox assignments, internal notes)
 * tutto si riduce a `chat_identifier_e164` (E.164 senza +, senza suffisso).
 *
 * Riusa `normalizeChatId` da `whatsappHelpers.ts` per coerenza con codice WAHA esistente.
 */

import {
  normalizeChatId,
  ensureFullChatId,
  chatIdsMatch,
} from '../../utils/whatsappHelpers';

export type WhatsAppProviderType = 'whatsapp' | 'whatsapp_cloud' | 'whatsapp_cloud_coex';

export interface NormalizedChatIdentity {
  /** E.164 senza + e senza suffissi. "393331234567". Vuoto per gruppi WAHA / unrecognized. */
  e164: string;
  /** ID nel formato originale del provider. Per WAHA: "393331234567@c.us". Per Cloud: "393331234567". */
  providerNativeId: string;
  /** Provider che ha generato questo identifier. */
  provider: WhatsAppProviderType;
  /** True se è un gruppo WhatsApp (solo WAHA — Cloud non supporta gruppi via API). */
  isGroup: boolean;
  /** True se è un identifier @lid WAHA (linked device, da risolvere via lidResolution). */
  isLinkedDevice: boolean;
}

/**
 * Converte un identifier raw (di qualsiasi provider) in struttura normalizzata
 * con E.164 estratto + metadati. Da usare in tutti i punti dove serve cross-provider join.
 *
 * Esempi:
 *   normalizeIdentity('393331234567@c.us', 'whatsapp')
 *     → { e164: '393331234567', providerNativeId: '393331234567@c.us', provider: 'whatsapp',
 *         isGroup: false, isLinkedDevice: false }
 *
 *   normalizeIdentity('120363012345@g.us', 'whatsapp')
 *     → { e164: '', providerNativeId: '120363012345@g.us', provider: 'whatsapp',
 *         isGroup: true, isLinkedDevice: false }
 *
 *   normalizeIdentity('393331234567', 'whatsapp_cloud')
 *     → { e164: '393331234567', providerNativeId: '393331234567', provider: 'whatsapp_cloud',
 *         isGroup: false, isLinkedDevice: false }
 */
export function normalizeIdentity(
  rawId: string,
  provider: WhatsAppProviderType
): NormalizedChatIdentity {
  if (!rawId) {
    return {
      e164: '',
      providerNativeId: '',
      provider,
      isGroup: false,
      isLinkedDevice: false,
    };
  }

  const isGroup = rawId.endsWith('@g.us');
  const isLinkedDevice = rawId.endsWith('@lid');

  // Per gruppi non c'è E.164 (l'id è opaco group ID)
  if (isGroup) {
    return {
      e164: '',
      providerNativeId: rawId,
      provider,
      isGroup: true,
      isLinkedDevice: false,
    };
  }

  // Riusa normalizeChatId esistente: estrae E.164 da @c.us / @s.whatsapp.net / @lid / no-suffix
  const e164 = normalizeChatId(rawId);

  return {
    e164,
    providerNativeId: rawId,
    provider,
    isGroup: false,
    isLinkedDevice,
  };
}

/**
 * Costruisce l'identifier nativo di un provider partendo da un E.164.
 * Usato quando si invia da Cloud verso un cliente che è in DB come WAHA, o viceversa.
 *
 * Esempi:
 *   toProviderNativeId('393331234567', 'whatsapp')        → '393331234567@c.us'
 *   toProviderNativeId('393331234567', 'whatsapp_cloud')  → '393331234567'
 *   toProviderNativeId('120363012345', 'whatsapp', { isGroup: true }) → '120363012345@g.us'
 */
export function toProviderNativeId(
  e164OrId: string,
  provider: WhatsAppProviderType,
  opts: { isGroup?: boolean } = {}
): string {
  if (!e164OrId) return '';

  const cleaned = normalizeChatId(e164OrId);

  if (opts.isGroup) {
    // Per gruppi WAHA: ricostruisci @g.us. Cloud non supporta gruppi.
    return `${cleaned}@g.us`;
  }

  if (provider === 'whatsapp') {
    // WAHA usa formato JID
    return ensureFullChatId(cleaned);
  }

  // Cloud (standalone o coex) usa E.164 puro come wa_id
  return cleaned;
}

/**
 * Estrae E.164 da QUALSIASI formato identifier (WAHA JID, Cloud wa_id, plain phone).
 * Shorthand per `normalizeIdentity(id, 'whatsapp').e164` ma più diretto per query DB.
 *
 * Esempi:
 *   e164FromAny('393331234567@c.us') → '393331234567'
 *   e164FromAny('+393331234567')      → '393331234567'  (rimuove +)
 *   e164FromAny('393331234567')        → '393331234567'
 *   e164FromAny('120363012345@g.us')   → ''  (gruppo, no E.164)
 */
export function e164FromAny(rawId: string | null | undefined): string {
  if (!rawId) return '';
  if (rawId.endsWith('@g.us')) return ''; // gruppi non hanno E.164
  // Rimuove eventuale + iniziale (E.164 internazionale comune)
  const stripped = rawId.startsWith('+') ? rawId.slice(1) : rawId;
  return normalizeChatId(stripped);
}

/**
 * Confronta due identifier di provider potenzialmente diversi (es. WAHA "393x@c.us" vs Cloud "393x").
 * Riusa `chatIdsMatch` per coerenza con il pattern WAHA esistente.
 */
export function identifiersMatchCrossProvider(
  id1: string | null | undefined,
  id2: string | null | undefined
): boolean {
  return chatIdsMatch(id1, id2);
}
